import axios from "axios";
import API_BASE_URL from "../config";
import { TENANT_AUTH } from '../authMode';

let accessToken = null;
let refreshPromise = null;
let sessionLost = () => {};
const authTransport = axios.create({ baseURL: API_BASE_URL, withCredentials: true,
  headers: { 'X-JV-Request': '1', 'Content-Type': 'application/json' } });
export function setSessionLostHandler(handler) { sessionLost = handler; }
export function clearAccessToken() { accessToken = null; }
export async function getLoginConfig() {
  const response = await authTransport.get('/auth/config');
  if (typeof response.data?.turnstile_enabled !== 'boolean') {
    throw new Error('Ungültige Anmeldekonfiguration.');
  }
  return response.data;
}
export async function authLogin(payload) {
  const response = await authTransport.post('/auth/login', payload);
  accessToken = response.data.access_token;
  return response.data.user;
}
export async function authLogout() {
  await authTransport.post('/auth/logout', {}, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  accessToken = null;
}
async function rotateAccess() {
  const response = await authTransport.post('/auth/refresh', {});
  accessToken = response.data.access_token;
  return response.data.user;
}
export async function authSwitchTenant(tenantId) {
  if (refreshPromise) await refreshPromise;
  // Keep refresh AND switch under one lock: another tab must not replace the
  // cookie/session between obtaining this JWT and submitting the switch.
  const change = async () => {
    try { await rotateAccess(); }
    catch (error) { accessToken = null; sessionLost(); throw error; }
    const response = await authTransport.post('/auth/switch-tenant', { mandant_id: Number(tenantId) }, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    accessToken = response.data.access_token;
    return response.data.user;
  };
  const operation = navigator.locks?.request ? navigator.locks.request('jv-session-refresh', change) : change();
  const pending = operation.finally(() => { if (refreshPromise === pending) refreshPromise = null; });
  refreshPromise = pending;
  return pending;
}
export function refreshSession() {
  if (!refreshPromise) {
    // Serialize refresh across same-origin tabs as well as concurrent requests.
    const operation = navigator.locks?.request
      ? navigator.locks.request('jv-session-refresh', rotateAccess) : rotateAccess();
    refreshPromise = operation.catch(error => {
      accessToken = null;
      sessionLost();
      throw error;
    }).finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

// Basis-Client
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Token automatisch anhängen
api.interceptors.request.use((config) => {
  const token = TENANT_AUTH ? accessToken : localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Optional: Globale Fehlerbehandlung
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Token abgelaufen → automatisch ausloggen
    const status = error.response?.status;
    const isLogin = error.config?.url === "/login";
    if (TENANT_AUTH) {
      // Passive status polling must not keep an unattended session alive.
      if (error.config?.backgroundAuth) return Promise.reject(error);
      if (status === 401 && error.config && !error.config._authRetried) {
        await refreshSession();
        return api({ ...error.config, _authRetried: true });
      }
      if (status === 401 || status === 422) { accessToken = null; sessionLost(); }
      return Promise.reject(error);
    }
    // Flask-JWT-Extended returns 422 for malformed/invalid JWTs.
    if (!isLogin && (status === 401 || status === 422)) {
      localStorage.removeItem("token");
      window.location.replace("/login");
    }
    return Promise.reject(error);
  }
);

export default api;
